import * as React from 'react';
import { cn } from '@teivor/shared-ui';

/**
 * Standard content frame for every client-portal page — the counterpart to the admin
 * app's `components/PageContainer.tsx`, and for the same reason.
 *
 * Every portal page used to open with its own `mx-auto flex max-w-Nxl … p-4 md:p-8`,
 * which had two consequences:
 *
 *   1. **A centred column that shrank as the monitor grew.** `max-w-4xl` is 896px. On a
 *      2560px display that is a 900px strip floating in the middle with ~770px of dead
 *      canvas either side — measured at 1601px wasted (66% of the content column) on
 *      `/leads` by `node scripts/wide/audit-wide.mjs`. The admin app had already solved
 *      this centrally; the portal never got the same treatment, so it was the worse of
 *      the two apps on a large screen by some margin.
 *   2. **Double padding.** The shell's `<main>` already applies `px-4 py-4 md:px-6 md:py-6`
 *      and these frames are its CHILD, so both insets stacked — 16+16px a side on a phone
 *      where iOS spends 16 total.
 *
 * Like the admin container this is **left-anchored** (no `mx-auto`): content hugs the
 * navigation instead of drifting away from it, and the cap is a STEPPED scale so a large
 * monitor gets a wider column rather than the same one a laptop gets. Tune width HERE —
 * pages must not re-introduce their own caps.
 *
 * width:
 *   'default' — dashboards, lists, tables, card grids (scales with the display)
 *   'narrow'  — approval detail, chat, account forms (reading width; deliberately does
 *               NOT scale — a 2400px-wide paragraph or text input is worse, not better)
 *   'full'    — edge-to-edge (rare)
 */
export type PageWidth = 'default' | 'narrow' | 'full';

export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: PageWidth;
}

export function PageContainer({
  width = 'default',
  className,
  style,
  children,
  ...rest
}: PageContainerProps) {
  const cap =
    width === 'full'
      ? 'max-w-none'
      : width === 'narrow'
        ? 'max-w-3xl'
        : 'max-w-[1640px] 3xl:max-w-[2180px] 4xl:max-w-none';
  return (
    <div
      // Declares the frame's width intent to the DOM so the wide-screen audit can tell a
      // reading-width page (deliberately narrow) from one that is narrow by accident.
      data-page-width={width}
      className={cn(
        'flex w-full flex-col gap-6',
        // No horizontal padding on a phone — the shell's <main> already insets by 16px and
        // this frame is its child. Desktop keeps a modest extra gutter, which it has room for.
        'py-2 sm:px-2',
        cap,
        className,
      )}
      style={{ fontFamily: 'var(--tvl-font-ui)', ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}
